import { LocationDto } from '@trucktrax/trucktrax-ts-common';

import recordLog, { LogLevel } from './errorUtil';
import { isValidLatLng, locationToLatLng } from './geoUtil';

/**
 * Google Places Autocomplete helpers. Kept out of adminUtil so the delivery-location
 * feature's address lookup lives on its own seam (mirrors geoUtil / deliveryLocationUtil)
 * and stays trivially mockable. Uses the legacy AutocompleteService / PlacesService because
 * the pinned @types/google.maps (3.45.2) has no types for the newer AutocompleteSuggestion
 * API; the session-token billing model is identical.
 */

// A single address prediction row shown in a pick-list. `placeId` is resolved to
// coordinates later via resolveAddressLocation.
export interface AddressSuggestion {
  placeId: string;
  primaryText: string;
  secondaryText: string;
  description: string;
}

// Result of resolving a picked prediction to coordinates + canonical address.
export interface ResolvedAddress {
  location: LocationDto;
  formattedAddress: string;
}

// Caller-owned lookup policy — biasing + result filtering are the feature's choice,
// not baked into this shared helper.
export interface AddressSuggestionOptions {
  sessionToken: google.maps.places.AutocompleteSessionToken;
  // Coordinates to rank nearby results first (e.g. the ticket's current location).
  bias?: LocationDto;
  biasRadiusMeters?: number;
  // ISO country code(s) to restrict predictions to (e.g. 'us').
  countryRestriction?: string | string[];
  // Places prediction types (e.g. ['address']).
  types?: string[];
}

/**
 * Session token bundles a search's keystroke predictions + the one Place Details lookup
 * into a single billed session. Create one per search, pass the SAME token to both
 * getAddressSuggestions and resolveAddressLocation; the session ends when getDetails runs,
 * so mint a fresh token for the next search.
 */
export const createAutocompleteSessionToken = (): google.maps.places.AutocompleteSessionToken =>
  new google.maps.places.AutocompleteSessionToken();

/**
 * As-you-type address predictions. Unlike the Geocoder, this returns the multiple candidate
 * addresses a partial query matches (e.g. the several "123 9th Ave" across cities), which is
 * what a pick-list needs.
 */
export const getAddressSuggestions = (
  input: string,
  options: AddressSuggestionOptions,
  onResult: (suggestions: AddressSuggestion[]) => void,
): void => {
  const { sessionToken, bias, biasRadiusMeters, countryRestriction, types } = options;
  const service = new google.maps.places.AutocompleteService();

  const { lat, lng } = locationToLatLng(bias ?? {});
  const biasLatLng = bias && isValidLatLng(lat, lng) ? new google.maps.LatLng(lat, lng) : undefined;

  service.getPlacePredictions(
    {
      input,
      sessionToken,
      ...(types ? { types } : {}),
      ...(countryRestriction ? { componentRestrictions: { country: countryRestriction } } : {}),
      ...(biasLatLng && biasRadiusMeters ? { location: biasLatLng, radius: biasRadiusMeters } : {}),
    },
    (predictions, status) => {
      if (status !== google.maps.places.PlacesServiceStatus.OK || !predictions) {
        // ZERO_RESULTS is an expected "no matches" outcome, not an error to log.
        if (status !== google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
          recordLog(LogLevel.Error, 'Error retrieving address predictions from Places API', {
            status,
            input,
          });
        }
        onResult([]);
        return;
      }
      onResult(
        predictions.map(p => ({
          placeId: p.place_id,
          primaryText: p.structured_formatting?.main_text ?? p.description,
          secondaryText: p.structured_formatting?.secondary_text ?? '',
          description: p.description,
        })),
      );
    },
  );
};

/**
 * Resolve a picked prediction to coordinates + the canonical formatted address. Passing the
 * search's sessionToken closes that autocomplete session so the keystrokes + this lookup bill
 * as one. A detached div satisfies the PlacesService constructor without a rendered map.
 */
export const resolveAddressLocation = (
  placeId: string,
  sessionToken: google.maps.places.AutocompleteSessionToken | undefined,
  onResult: (resolved: ResolvedAddress | null) => void,
): void => {
  const service = new google.maps.places.PlacesService(document.createElement('div'));
  service.getDetails(
    { placeId, fields: ['geometry', 'formatted_address'], sessionToken },
    (place, status) => {
      const loc = place?.geometry?.location;
      if (status !== google.maps.places.PlacesServiceStatus.OK || !loc) {
        recordLog(LogLevel.Error, 'Error retrieving place details from Places API', {
          status,
          placeId,
        });
        onResult(null);
        return;
      }
      onResult({
        location: { latitude: loc.lat(), longitude: loc.lng() },
        formattedAddress: place?.formatted_address ?? '',
      });
    },
  );
};
